import { isDataTransferSupported, setDataTransfer, setInputFiles } from '@tesla/design-system';
import { useRef, useState } from 'react';
/**
 * A hook for managing file upload state. Handles updating a file input's files with a DataTransfer object.
 * Returns a ref, a boolean if the input has files that were rejected, an array of rejected files, and an
 * array of accepted files, handlers, and the actual setFileList method.
 */
export const useFileUploadState = ({ onSetFileList, } = {}) => {
    const inputRef = useRef(null);
    const [fileNames, setFileNames] = useState([]);
    const [files, setFiles] = useState([]);
    const [fileSelectErrors, setFileSelectErrors] = useState(null);
    // DataTransfer constructor is not supported in Safari 13.
    // without it, this allows the behavior to fall back on the native file input
    const dataTransferSupported = isDataTransferSupported();
    // Pass an HTML file input, a file name to remove from the file input's files list
    const setFileList = (fileInput, files, fileNameToExclude) => {
        if (!fileInput || !fileInput.files) {
            return;
        }
        // construct a data transfer. Note max size is passed from the file input data attribute
        // set dataTransfer accepts a fileList, but will convert the fileList to an array.
        const { dataTransfer, errors, fileNames } = setDataTransfer(dataTransferSupported ? Array.from(files) : Array.from(fileInput.files), {
            maxFileSize: Number(fileInput.dataset.maxFileSize),
            maxFiles: Number(fileInput.dataset.maxFiles),
        }, fileNameToExclude || null);
        // Set errors if we have them
        setFileSelectErrors(errors);
        // Update the actual input.files
        setInputFiles(dataTransfer, fileInput);
        // Re-render the list of files
        setFileNames(fileNames);
        // Track the actual files
        const fileData = dataTransfer ? Array.from(dataTransfer.files) : Array.from(fileInput.files);
        setFiles(fileData);
        // Call setter if passed
        if (onSetFileList)
            onSetFileList({ fileNames, fileSelectErrors: errors, files: fileData });
    };
    const handleInputChange = () => {
        if (!inputRef || !inputRef.current) {
            return;
        }
        // convert the input files list
        const newFiles = Array.from(inputRef.current.files || []);
        // If this is a single file upload only take one file
        if (!inputRef.current.hasAttribute('multiple')) {
            setFileList(inputRef.current, newFiles.slice(0, 1));
            return;
        }
        // This ensures we are not adding two files with the same name
        const updatedFiles = [...files, ...newFiles].filter((v, i, a) => a.findIndex((t) => t.name === v.name) === i);
        setFileList(inputRef.current, updatedFiles);
    };
    // This passes the list of files, and a file to remove.
    // setFileList handles special behavior in preventing the onChange
    // from executing twice when changing the file list changes to the acutal input
    const handleItemRemove = (e) => {
        setFileList(inputRef.current, [...files], e.currentTarget.dataset.name);
    };
    /**
     * Handle Error message is a helper to render an error message from the errors
     * exposed by the component
     *
     * @param errors the errors returned from use state
     * @returns function that accepts an object of responses to handle:
     * maxFilesExceeded, maxFileSizeExceeded, required
     * Responses can be a string, or a function that accepts an array of filenames
     */
    const handleErrorMessage = (errors) => {
        return (responses) => {
            const key = Object.keys(errors)[0];
            switch (typeof responses[key]) {
                case 'function':
                    return responses[key].call(null, errors[key] || ['']);
                case 'string':
                    return responses[key];
                default:
                    return;
            }
        };
    };
    return {
        fileNames,
        fileSelectErrors,
        files,
        handleErrorMessage,
        handleInputChange,
        handleItemRemove,
        inputRef,
        setFileList,
        setFileSelectErrors,
    };
};
